/**
 * MessageBadge — chat icon in TopNav with unread badge.
 * Phase 26b: Popup with inline reply capability.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUnreadMessages, sendMessage, fetchMessages } from '../../api/messages';
import { useAuth } from '../../context/AuthContext';

export default function MessageBadge() {
  const { user: currentUser } = useAuth();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ total_unread: 0, jobs: [] });
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState(null); // {job_id, job_number, sender, message, conversation_id, sender_initials}
  const [popupMessages, setPopupMessages] = useState([]);
  const [popupLoading, setPopupLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const popupEndRef = useRef(null);
  const prevCountRef = useRef(0);
  const drawerRef = useRef(null);
  const replyInputRef = useRef(null);
  const navigate = useNavigate();

  // Poll every 30s
  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const result = await getUnreadMessages();
        if (cancelled) return;
        const newCount = result.total_unread || 0;

        // Popup when count increases
        if (newCount > prevCountRef.current && prevCountRef.current >= 0 && result.jobs?.length > 0) {
          const latest = result.jobs[0];
          setPopup({
            job_id: latest.job_id,
            job_number: latest.job_number,
            customer_name: latest.customer_name,
            sender: latest.latest_sender,
            sender_initials: latest.latest_sender_initials || latest.latest_sender?.split(' ').map(w => w[0] || '').join('').toUpperCase().slice(0, 2) || '??',
            message: latest.latest_message,
            conversation_id: latest.conversation_id,
          });
          setReplyText('');
        }

        prevCountRef.current = newCount;
        setCount(newCount);
        setData(result);
      } catch {}
    }
    prevCountRef.current = -1;
    poll().then(() => { prevCountRef.current = count; });
    const interval = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Load on dropdown open
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getUnreadMessages().then(r => { setData(r); setCount(r.total_unread || 0); }).catch(() => {}).finally(() => setLoading(false));
  }, [open]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    function h(e) { if (drawerRef.current && !drawerRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  // Load conversation messages + focus reply when popup opens
  useEffect(() => {
    if (!popup?.conversation_id) { setPopupMessages([]); return; }
    setPopupLoading(true);
    fetchMessages(popup.job_id, popup.conversation_id)
      .then(msgs => {
        setPopupMessages(msgs);
        setTimeout(() => {
          popupEndRef.current?.scrollIntoView({ behavior: 'auto' });
          replyInputRef.current?.focus();
        }, 100);
      })
      .catch(() => setPopupMessages([]))
      .finally(() => setPopupLoading(false));
  }, [popup?.conversation_id]);

  async function handleReply() {
    if (!replyText.trim() || !popup?.conversation_id) return;
    setReplying(true);
    try {
      await sendMessage(popup.job_id, popup.conversation_id, { body: replyText.trim() });
      setReplyText('');
      // Reload messages in popup
      try {
        const msgs = await fetchMessages(popup.job_id, popup.conversation_id);
        setPopupMessages(msgs);
        setTimeout(() => popupEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      } catch {}
      // Refresh counts
      const result = await getUnreadMessages();
      setData(result);
      setCount(result.total_unread || 0);
      prevCountRef.current = result.total_unread || 0;
    } catch (err) {
      alert(err.response?.data?.error || 'Reply failed');
    } finally { setReplying(false); }
  }

  return (
    <>
      {/* ── Popup Modal (full conversation) ── */}
      {popup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30" onClick={() => setPopup(null)}>
          <div className="bg-white rounded-xl shadow-2xl border border-blue-200 w-full max-w-lg mx-4 animate-slide-in flex flex-col" style={{ maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 bg-blue-50 rounded-t-xl flex-shrink-0">
              <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-gray-900">Job #{popup.job_number}</h3>
                <p className="text-xs text-gray-500">{popup.customer_name}</p>
              </div>
              <button onClick={() => setPopup(null)} className="text-gray-400 hover:text-gray-600 p-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Message history */}
            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2" style={{ minHeight: '200px' }}>
              {popupLoading ? (
                <div className="flex justify-center py-8 text-sm text-gray-400">Loading...</div>
              ) : popupMessages.length === 0 ? (
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-gray-400 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">{popup.sender_initials}</div>
                  <div className="flex-1">
                    <div className="text-xs font-medium text-gray-600">{popup.sender}</div>
                    <div className="bg-gray-100 rounded-2xl rounded-tl-md px-3 py-1.5 mt-0.5">
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{popup.message}</p>
                    </div>
                  </div>
                </div>
              ) : (
                popupMessages.map((msg) => {
                  const isMine = currentUser && msg.sender === currentUser.id;
                  const initials = msg.sender_initials || '??';
                  return (
                    <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      {!isMine && (
                        <div className="w-7 h-7 rounded-full bg-gray-400 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0 mr-2 mt-auto">{initials}</div>
                      )}
                      <div className={`max-w-[75%] flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                        <span className={`text-[10px] font-medium mb-0.5 ${isMine ? 'text-blue-500' : 'text-gray-500'}`}>{msg.sender_name}</span>
                        <div className={`px-3 py-1.5 rounded-2xl text-sm ${isMine ? 'bg-blue-600 text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md'}`}>
                          <p className="whitespace-pre-wrap break-words">{msg.body}</p>
                        </div>
                        <span className="text-[9px] text-gray-400 mt-0.5">{_fmtTime(msg.created_at)}</span>
                      </div>
                      {isMine && (
                        <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0 ml-2 mt-auto">{initials}</div>
                      )}
                    </div>
                  );
                })
              )}
              <div ref={popupEndRef} />
            </div>

            {/* Reply input */}
            <div className="px-5 py-3 border-t border-gray-100 flex-shrink-0">
              <div className="flex items-end gap-2">
                <textarea
                  ref={replyInputRef}
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); } }}
                  placeholder="Type a reply..."
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                />
                <button
                  onClick={handleReply}
                  disabled={replying || !replyText.trim()}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors"
                >
                  {replying ? '...' : 'Send'}
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-2 border-t border-gray-100 bg-gray-50 rounded-b-xl flex justify-between items-center flex-shrink-0">
              <button
                onClick={() => { setPopup(null); navigate(`/jobs/${popup.job_id}`); }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Open Job →
              </button>
              <button onClick={() => setPopup(null)} className="text-sm text-gray-500 hover:text-gray-700">
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Badge ── */}
      <div className="relative" ref={drawerRef}>
        <button
          onClick={() => setOpen(o => !o)}
          className="relative p-1.5 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-50"
          title="Job Messages"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
              {count > 99 ? '99+' : count}
            </span>
          )}
        </button>

        {/* Dropdown */}
        {open && (
          <div className="absolute right-0 top-9 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 flex flex-col max-h-[400px]">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <span className="font-semibold text-sm text-gray-900">Messages</span>
              {count > 0 && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">{count} unread</span>}
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="py-8 text-center text-sm text-gray-400">Loading...</div>
              ) : data.jobs?.length > 0 ? (
                data.jobs.map(j => (
                  <button
                    key={j.job_id}
                    onClick={() => { setOpen(false); navigate(`/jobs/${j.job_id}`); }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-sm font-semibold text-gray-900">Job #{j.job_number}</span>
                      <span className="min-w-[20px] h-5 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1.5">{j.unread_count}</span>
                    </div>
                    <p className="text-xs text-gray-500">{j.customer_name}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="w-5 h-5 rounded-full bg-gray-300 text-white flex items-center justify-center text-[8px] font-bold">{j.latest_sender_initials || '??'}</span>
                      <p className="text-xs text-gray-600 truncate flex-1">{j.latest_message}</p>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5">{_formatAge(j.latest_at)}</p>
                  </button>
                ))
              ) : (
                <div className="py-12 text-center">
                  <p className="text-sm text-gray-400">No unread messages</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function _formatAge(d) {
  if (!d) return '';
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function _fmtTime(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}
